import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Upload, CheckCircle2, AlertTriangle } from "lucide-react";

const TABS = [
  { key: "customer", label: "Customer Account" },
  { key: "employee", label: "Employee Account" },
];

export default function Signup() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [tab, setTab] = useState("customer");

  // Customer state
  const [c, setC] = useState({
    email: "", password: "", name: "", dob: "", phone: "",
    id_document_data_url: "", agreed_terms: false, agreed_21_plus: false,
  });
  const [idPreview, setIdPreview] = useState(null);

  // Employee state
  const [e, setE] = useState({ email: "", password: "", name: "", pin: "" });

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onIdSelect = (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { setError("ID image must be under 6MB."); return; }
    const r = new FileReader();
    r.onload = () => {
      setC((s) => ({ ...s, id_document_data_url: r.result }));
      setIdPreview(r.result);
    };
    r.readAsDataURL(file);
  };

  const submitCustomer = async (ev) => {
    ev.preventDefault();
    setError(""); setBusy(true);
    try {
      const res = await api.post("/auth/register/customer", c);
      localStorage.setItem("hat_token", res.data.access_token);
      localStorage.setItem("hat_user", JSON.stringify(res.data.user));
      await auth?.refresh?.();
      navigate("/account");
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-up failed.");
    } finally { setBusy(false); }
  };

  const submitEmployee = async (ev) => {
    ev.preventDefault();
    setError(""); setBusy(true);
    try {
      const res = await api.post("/auth/register/employee", e);
      localStorage.setItem("hat_token", res.data.access_token);
      localStorage.setItem("hat_user", JSON.stringify(res.data.user));
      navigate("/admin");
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-up failed.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="signup-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <div className="bg-sand/90 backdrop-blur-sm border border-tan/50 p-8 lg:p-12 float-card">
        <span className="eyebrow">Create an Account</span>
        <h1 className="font-serif text-4xl text-navy mt-3">Join High Achievers.</h1>
        <p className="mt-3 text-charcoal/80">An account is required before placing any pickup or shipping order.</p>

        {/* Tabs */}
        <div className="mt-8 flex gap-1 border-b border-tan/40">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => { setTab(t.key); setError(""); }}
              className={`px-5 py-3 text-xs uppercase tracking-[0.2em] ${tab === t.key ? "text-navy border-b-2 border-navy" : "text-charcoal/60 hover:text-navy"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className="mt-6 bg-texasred/10 border border-texasred/40 text-texasred px-4 py-3 text-sm flex gap-2"><AlertTriangle size={16}/> {error}</div>}

        {/* CUSTOMER */}
        {tab === "customer" && (
          <form onSubmit={submitCustomer} className="mt-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name"><input data-testid="cust-name" required value={c.name} onChange={(ev) => setC({ ...c, name: ev.target.value })} className={inp}/></Field>
              <Field label="Email"><input data-testid="cust-email" type="email" required value={c.email} onChange={(ev) => setC({ ...c, email: ev.target.value })} className={inp}/></Field>
              <Field label="Password (8+ chars)"><input data-testid="cust-password" type="password" minLength={8} required value={c.password} onChange={(ev) => setC({ ...c, password: ev.target.value })} className={inp}/></Field>
              <Field label="Phone"><input data-testid="cust-phone" value={c.phone} onChange={(ev) => setC({ ...c, phone: ev.target.value })} className={inp}/></Field>
              <Field label="Date of birth (you must be 21+)"><input data-testid="cust-dob" type="date" required value={c.dob} onChange={(ev) => setC({ ...c, dob: ev.target.value })} className={inp}/></Field>
            </div>

            <div className="mt-2">
              <label className="text-xs uppercase tracking-wide text-charcoal/80">Photo of government-issued ID or driver's license</label>
              <p className="text-xs text-charcoal/65 mt-1">Required to verify you are 21+. Stored securely and used solely for age verification.</p>
              <label data-testid="cust-id-upload" className="mt-3 flex items-center gap-3 border-2 border-dashed border-tan/60 px-5 py-6 cursor-pointer hover:border-navy bg-white">
                <Upload size={20} className="text-navy"/>
                <span className="text-sm text-charcoal/85">{idPreview ? "Replace photo" : "Tap to upload photo of your ID"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(ev) => onIdSelect(ev.target.files?.[0])} />
              </label>
              {idPreview && <img src={idPreview} alt="ID preview" className="mt-3 max-h-44 border border-tan/40" />}
            </div>

            <label className="flex gap-3 items-start text-sm text-charcoal/85 mt-3">
              <input data-testid="cust-agree-21" type="checkbox" checked={c.agreed_21_plus} onChange={(ev) => setC({ ...c, agreed_21_plus: ev.target.checked })} className="mt-1"/>
              <span>I confirm I am 21 years of age or older and that the ID I uploaded is true and accurate.</span>
            </label>
            <label className="flex gap-3 items-start text-sm text-charcoal/85">
              <input data-testid="cust-agree-terms" type="checkbox" checked={c.agreed_terms} onChange={(ev) => setC({ ...c, agreed_terms: ev.target.checked })} className="mt-1"/>
              <span>I agree to the <Link to="/policies/terms" className="underline">Terms and Conditions</Link>, <Link to="/policies/privacy" className="underline">Privacy Policy</Link>, and <Link to="/policies/refund" className="underline">Refund Policy</Link>.</span>
            </label>

            <button data-testid="cust-submit" disabled={busy} className="mt-5 w-full bg-navy text-white px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-charcoal disabled:opacity-50">
              {busy ? "Creating account…" : "Create Customer Account"}
            </button>
            <p className="text-xs text-charcoal/70 text-center mt-3">Already have an account? <Link to="/login" className="underline">Sign in</Link></p>
          </form>
        )}

        {/* EMPLOYEE */}
        {tab === "employee" && (
          <form onSubmit={submitEmployee} className="mt-6 space-y-4">
            <Field label="Full name"><input data-testid="emp-name" required value={e.name} onChange={(ev) => setE({ ...e, name: ev.target.value })} className={inp}/></Field>
            <Field label="Email"><input data-testid="emp-email" type="email" required value={e.email} onChange={(ev) => setE({ ...e, email: ev.target.value })} className={inp}/></Field>
            <Field label="Password (8+ chars)"><input data-testid="emp-password" type="password" minLength={8} required value={e.password} onChange={(ev) => setE({ ...e, password: ev.target.value })} className={inp}/></Field>
            <Field label="Employee PIN (required)">
              <input data-testid="emp-pin" type="password" required value={e.pin} onChange={(ev) => setE({ ...e, pin: ev.target.value })} className={inp} placeholder="6-digit PIN provided by management"/>
            </Field>
            <button data-testid="emp-submit" disabled={busy} className="mt-3 w-full bg-navy text-white px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-charcoal disabled:opacity-50">
              {busy ? "Creating account…" : "Create Employee Account"}
            </button>
            <p className="text-xs text-charcoal/70 text-center mt-2">Without the correct PIN you cannot register as an employee.</p>
          </form>
        )}

        {/* Legal notices */}
        <div className="mt-10 pt-8 border-t border-tan/40 space-y-3 text-[11.5px] leading-relaxed text-charcoal/75">
          <p><strong className="text-navy">21+ Only.</strong> By creating an account you affirm you are at least 21 years of age and a U.S. resident.</p>
          <p><strong className="text-navy">FDA Notice.</strong> These products have not been evaluated by the Food and Drug Administration. They are not intended to diagnose, treat, cure, or prevent any disease.</p>
          <p><strong className="text-navy">Hemp Compliance.</strong> All hemp-derived products sold contain less than 0.3% Delta-9 THC by dry weight, per the 2018 U.S. Farm Bill. Shipping restrictions vary by state; restricted states will be blocked at checkout.</p>
          <p><strong className="text-navy">Identity Verification.</strong> Your uploaded ID is encrypted at rest and used solely to verify age and prevent fraud. We will never sell or share this information.</p>
          <p><strong className="text-navy">No Operating Vehicles.</strong> Do not operate vehicles or heavy machinery after use of these products. Consult your physician before use if pregnant, nursing, or taking medication.</p>
          <p><strong className="text-navy">Texas Notice.</strong> High Achievers Texarkana TX operates in compliance with current Texas hemp retail rules. You agree to comply with all federal, state, and local laws applicable to the purchase and use of these products.</p>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full border border-tan/50 px-4 py-3 bg-white focus:border-navy focus:outline-none";
const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wide text-charcoal/80">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);
